"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { TrendingUp, PiggyBank, CheckCircle, Clock, ArrowRight, AlertTriangle, Zap } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const benefits = [{
  title: "Qualidade Cinematográfica",
  description: "Equipamentos de última geração que garantem uma estética profissional e envolvente para seu conteúdo.",
  icon: Zap
}, {
  title: "Acústica de Alta Performance",
  description: "Ambiente tratado para captação de áudio cristalina, eliminando ruídos e garantindo clareza total.",
  icon: CheckCircle
}, {
  title: "Fluxo de Trabalho Ágil",
  description: "Processos otimizados desde a gravação até a entrega final, respeitando seus prazos de publicação.",
  icon: Clock
}, {
  title: "Suporte Técnico Especializado",
  description: "Nossa equipe cuida de toda a complexidade técnica para você focar apenas na sua mensagem.",
  icon: TrendingUp
}];
export default function OperationalEfficiency() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="222059" id="diferencial" className="py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          {}
          <div ref={leftRef} className={`flex flex-col gap-8 transition-all duration-700 ease-out ${isLeftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div>
              <h2 className="font-default text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground">
                Integração com conteúdo
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed font-default">
                A transição do amador para o profissional é o diferencial que sua marca precisa. Descubra como nossa infraestrutura em Pinheiros eleva o patamar da sua presença digital.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="bg-muted/30 border-border shadow-none rounded-lg">
                <CardHeader className="pb-3">
                  <div className="size-10 rounded-full bg-destructive/10 flex items-center justify-center mb-2">
                    <AlertTriangle className="size-5 text-destructive" />
                  </div>
                  <CardTitle className="font-default text-lg text-muted-foreground">Produção Amadora</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-muted-foreground font-default">
                    <li className="flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-destructive/40 shrink-0" />
                      Áudio com eco e ruídos
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-destructive/40 shrink-0" />
                      Iluminação inconsistente
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-destructive/40 shrink-0" />
                      Edição limitada e lenta
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-primary/5 border-primary/10 shadow-sm relative overflow-hidden rounded-lg">
                <div className="absolute top-0 right-0 p-4 opacity-5">
                  <Zap className="size-24 text-primary" />
                </div>
                <CardHeader className="pb-3 relative z-10">
                  <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                    <Zap className="size-5 text-primary" />
                  </div>
                  <CardTitle className="font-default text-lg text-foreground">Nosso Estúdio</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <ul className="space-y-2 text-sm text-foreground font-default">
                    <li className="flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-primary shrink-0" />
                      Isolamento acústico total
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-primary shrink-0" />
                      Câmeras 4K e luz cinema
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-primary shrink-0" />
                      Direção técnica em tempo real
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="w-full sm:w-auto group bg-primary hover:bg-primary/90 text-primary-foreground rounded-full">
                <Link to="/StudioPage">
                  Conheça Nossa Estrutura
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`flex flex-col gap-6 transition-all duration-700 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-lg relative overflow-hidden">
              <div className="absolute inset-0 opacity-10 pointer-events-none">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645251/img/studio_pinheiros_tech.png" alt="Background Tech" className="w-full h-full object-cover" />
              </div>
              <h3 className="font-default text-xl font-bold mb-6 text-foreground relative z-10">Vantagens Exclusivas</h3>
              <div className="flex flex-col gap-6 relative z-10">
                {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex gap-4">
                    <div className="shrink-0 mt-1">
                      <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20">
                        <benefit.icon className="size-6 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-default font-semibold text-foreground mb-1">
                        {benefit.title}
                      </h4>
                      <p className="text-muted-foreground text-sm leading-relaxed font-default">
                        {benefit.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
            
            {}
            <div className="flex items-center justify-between px-4 py-3 bg-secondary/30 rounded-xl border border-border/50">
              <span className="text-sm font-medium text-secondary-foreground font-default">Pronto para elevar seu conteúdo?</span>
              <Button asChild variant="link" size="sm" className="px-0 text-primary font-semibold">
                <Link to="/ContactPage">
                  Falar com especialista
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}